import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Label,
  PageHeader,
  SkeletonRows,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import { isVideoKind } from '../lib/media';
import { MediaImg, MediaVideo } from '../components/MediaImg';
import {
  expertisePartLabel,
  expertiseStateLabel,
  fuelLabel,
  gearLabel,
} from '../lib/vehicle-labels';
import type { AdminSubmissionView, SubmissionStatus } from '@bia-auction/shared-schema';
import { adminSubmissionDetailSchema, adminSubmissionViewSchema } from '@bia-auction/shared-schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { AlertTriangle, Inbox } from 'lucide-react';
import { useState } from 'react';
import { ErrorMsg } from '../components/Feedback';
import { api, ApiError } from '../lib/api';
import { formatDateTime, formatKurus } from '../lib/format';

type StatusTab = 'queued' | 'approved' | 'rejected';

/** The view's `status` is the derived lot lifecycle (queued/scheduled/live/sold/unsold/
 * rejected) — richer than the queued/approved/rejected query filter. */
const STATUS_VARIANT: Record<SubmissionStatus, 'info' | 'live' | 'success' | 'neutral' | 'destructive'> = {
  queued: 'info',
  scheduled: 'info',
  live: 'live',
  sold: 'success',
  unsold: 'neutral',
  rejected: 'destructive',
};

export function SubmissionsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<StatusTab>('queued');
  const [review, setReview] = useState<AdminSubmissionView | null>(null);

  // Status tabs are frontend-only — the route filters any ?status= (submissions.ts:34).
  const q = useQuery({
    queryKey: ['submissions', tab],
    queryFn: () => api(`/admin/submissions?status=${tab}`, {}, z.array(adminSubmissionViewSchema)),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['submissions'] });
  const fail = (e: unknown) => toast(e instanceof ApiError ? e.message : t('common.error'), 'error');

  const approve = useMutation({
    mutationFn: (id: string) => api(`/admin/submissions/${id}/approve`, { method: 'POST' }),
    onSuccess: () => {
      toast(t('submissions.approved'), 'success');
      setReview(null);
      void invalidate();
    },
    onError: fail,
  });
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api(`/admin/submissions/${id}/reject`, { method: 'POST', body: { reason } }),
    onSuccess: () => {
      toast(t('submissions.rejected'), 'success');
      setReview(null);
      void invalidate();
    },
    onError: fail,
  });

  return (
    <div className="space-y-4">
      {/* The heading names the records, not the default tab: the page also lists approved and
          rejected ones. The subline answers "which submissions?" — firm-sent vehicles — and
          what a decision does, so the queue is legible without the help centre. */}
      <PageHeader title={t('submissions.title')} subline={t('submissions.subtitle')} />
      <Tabs value={tab} onValueChange={(v) => setTab(v as StatusTab)}>
        <TabsList>
          <TabsTrigger value="queued">{t('submissions.tab.queued')}</TabsTrigger>
          <TabsTrigger value="approved">{t('submissions.tab.approved')}</TabsTrigger>
          <TabsTrigger value="rejected">{t('submissions.tab.rejected')}</TabsTrigger>
        </TabsList>
      </Tabs>
      {q.isLoading ? (
        <SkeletonRows rows={5} />
      ) : q.isError ? (
        <ErrorMsg error={q.error} />
      ) : q.data && q.data.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('submissions.col.plate')}</TableHead>
              <TableHead>{t('submissions.col.vehicle')}</TableHead>
              {/* Who sent the car. The API has projected `companyName` since M13 and the
                  handbook already tells moderators the row carries it — the queue just
                  never rendered it. */}
              <TableHead>{t('submissions.col.company')}</TableHead>
              <TableHead>{t('submissions.col.start')}</TableHead>
              <TableHead>{t('vehicles.col.photo')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-mono text-xs">{s.plate ?? s.vehicleId.slice(0, 8)}</TableCell>
                <TableCell>{[s.brand, s.series].filter(Boolean).join(' ') || t('common.none')}</TableCell>
                <TableCell>{s.companyName ?? t('common.none')}</TableCell>
                <TableCell className="tabular-nums">
                  {s.startingPrice !== null ? formatKurus(s.startingPrice) : t('common.none')}
                </TableCell>
                <TableCell className="tabular-nums text-muted-foreground">
                  {s.mediaCount > 0
                    ? t('submissions.photoCount', { n: String(s.mediaCount) })
                    : t('submissions.noPhotos')}
                </TableCell>
                <TableCell className="space-x-1 text-right">
                  {/* Detail-before-decision (redesign-admin.md §9): review opens the dialog;
                      quick Approve stays on the row for speed while queued — outline, so the
                      list never shows N filled buttons (§8 one-primary-per-view; the dialog's
                      Approve is the filled one). */}
                  <Button variant="outline" size="sm" onClick={() => setReview(s)}>
                    {t('submissions.review')}
                  </Button>
                  {s.status === 'queued' && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={approve.isPending}
                      onClick={() => approve.mutate(s.id)}
                    >
                      {t('submissions.approve')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState icon={Inbox} title={t(`submissions.empty.${tab}`)} />
      )}

      {review && (
        <ReviewDialog
          submission={review}
          busy={approve.isPending || reject.isPending}
          onOpenChange={(o) => !o && setReview(null)}
          onApprove={() => approve.mutate(review.id)}
          onReject={(reason) => reject.mutate({ id: review.id, reason })}
        />
      )}
    </div>
  );
}

/** Full-payload review with Approve / Reject-with-reason (replaces the reject prompt()). */
function ReviewDialog({
  submission: s,
  busy,
  onOpenChange,
  onApprove,
  onReject,
}: {
  submission: AdminSubmissionView;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: () => void;
  onReject: (reason?: string) => void;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  // The full submitted vehicle (M11). The queue row carries three columns; a submission can
  // now arrive with a spec sheet, a description, an expertise report and photos, and
  // approving one on plate + brand alone is a decision made blind.
  const detail = useQuery({
    queryKey: ['submission', s.id],
    queryFn: () => api(`/admin/submissions/${s.id}`, {}, adminSubmissionDetailSchema),
  });
  const vehicle = detail.data?.vehicle;
  // `media` is the per-URL mirror report the ingestion pass recorded (present only when the
  // submit carried `mediaUrls[]`); only the failures are worth a moderator's screen.
  const failedMirrors = (detail.data?.media ?? []).filter((m) => !m.ok);

  const rows: Array<[string, string]> = [
    [t('submissions.col.plate'), s.plate ?? s.vehicleId],
    [t('submissions.col.vehicle'), [s.brand, s.series].filter(Boolean).join(' ') || t('common.none')],
    [t('submissions.col.company'), s.companyName ?? t('common.none')],
    [t('submissions.col.start'), s.startingPrice !== null ? formatKurus(s.startingPrice) : t('common.none')],
    [t('submissions.detail.clientRef'), s.clientRef ?? t('common.none')],
    [t('submissions.detail.created'), formatDateTime(s.createdAt)],
  ];
  if (vehicle) {
    const spec = [
      vehicle.year,
      vehicle.km !== null ? `${vehicle.km.toLocaleString('tr-TR')} km` : null,
      vehicle.fuel ? fuelLabel(vehicle.fuel) : null,
      vehicle.gear ? gearLabel(vehicle.gear) : null,
      vehicle.color,
    ]
      .filter(Boolean)
      .join(' · ');
    if (spec) rows.push([t('vehicles.section.technical'), spec]);
    if (vehicle.modelName) rows.push([t('vehicles.field.modelName'), vehicle.modelName]);
    if (vehicle.reserve != null) rows.push([t('vehicles.field.reserve'), formatKurus(vehicle.reserve)]);
    // The badge is derived from a COMPLETE all-original report — say which, rather than
    // showing a bare "no" that reads as "this car has damage".
    rows.push([
      t('expertise.title'),
      vehicle.isOriginal
        ? t('expertise.originalBadge')
        : vehicle.expertise && Object.keys(vehicle.expertise).length > 0
          ? Object.entries(vehicle.expertise)
              .filter(([, v]) => v !== 'original')
              .map(([k, v]) => `${expertisePartLabel(k as never)}: ${expertiseStateLabel(v as never)}`)
              .join(', ') || t('expertise.notAssessed')
          : t('expertise.notAssessed'),
    ]);
    if (vehicle.description) rows.push([t('vehicles.field.description'), vehicle.description]);
  }
  if (s.status !== 'queued') {
    rows.push([t('submissions.detail.decided'), formatDateTime(s.decidedAt)]);
    if (s.rejectReason) rows.push([t('submissions.rejectReason'), s.rejectReason]);
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('submissions.detailTitle')}</DialogTitle>
        </DialogHeader>
        {vehicle && vehicle.media.length > 0 && (
          <ul className="flex gap-2 overflow-x-auto pb-1">
            {vehicle.media.map((m) => (
              <li key={m.id} className="shrink-0">
                {/* A submission can carry a video or a damage video (`MEDIA_KINDS`); an
                    `<img>` pointed at one is a broken-image icon, not a fallback. */}
                {isVideoKind(m.kind) ? (
                  <MediaVideo
                    path={m.url}
                    controls
                    preload="metadata"
                    className="h-24 w-32 rounded-md border object-cover"
                  />
                ) : (
                  <MediaImg
                    path={m.url}
                    alt=""
                    loading="lazy"
                    className="h-24 w-32 rounded-md border object-cover"
                  />
                )}
              </li>
            ))}
          </ul>
        )}
        {/* The mirror report (ADR 0025 §2): when a client submitted `mediaUrls[]` that we
            could not fetch, the photo count alone hides it — the moderator would judge the
            car on two photos without knowing three more were meant to be there. */}
        {failedMirrors.length > 0 && (
          <div className="space-y-1 rounded-md border border-destructive p-2 text-sm">
            <p className="flex items-center gap-1.5 font-medium">
              <AlertTriangle aria-hidden className="h-4 w-4 text-destructive" />
              {t('submissions.mediaReport')} —{' '}
              {t('submissions.mediaReportFailed', { n: String(failedMirrors.length) })}
            </p>
            <ul className="space-y-0.5 text-muted-foreground">
              {failedMirrors.map((m) => (
                <li key={m.url} className="break-all">
                  {m.url}
                  {m.error ? ` — ${m.error}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="tabular-nums">{v}</dd>
            </div>
          ))}
          <div className="contents">
            <dt className="text-muted-foreground">{t('sales.col.status')}</dt>
            <dd>
              <StatusBadge variant={STATUS_VARIANT[s.status]}>
                {t(`submissions.status.${s.status}`)}
              </StatusBadge>
            </dd>
          </div>
        </dl>
        {rejecting && (
          <div className="space-y-1.5">
            <Label htmlFor="reject-reason">{t('submissions.rejectReason')}</Label>
            <Textarea
              id="reject-reason"
              autoFocus
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        )}
        {s.status === 'queued' && (
          <DialogFooter>
            {rejecting ? (
              <>
                <Button variant="outline" disabled={busy} onClick={() => setRejecting(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  disabled={busy}
                  onClick={() => onReject(reason.trim() || undefined)}
                >
                  {t('submissions.reject')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" disabled={busy} onClick={() => setRejecting(true)}>
                  {t('submissions.reject')}
                </Button>
                <Button disabled={busy} onClick={onApprove}>
                  {t('submissions.approve')}
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
